import { useCallback } from 'react';
import axios, { useApiRequest } from './axios';
import {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { LoginResponse, WebAuthnCredentialData } from '../types/auth';

const AUTHENTICATION_OPTIONS_URL = '/auth/webauthn/authentication/options/';
const AUTHENTICATION_URL = '/auth/webauthn/authentication/';
const REGISTRATION_OPTIONS_URL = '/auth/webauthn/registration/options/';
const REGISTRATION_URL = '/auth/webauthn/registration/';
export const WEBAUTHN_CREDENTIALS_URL = '/auth/webauthn/credentials/';

export const useWebAuthnAuthenticationMutation = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const optionsMutation = useCallback(async (email: string): Promise<{ data: PublicKeyCredentialRequestOptionsJSON | undefined, success: boolean }> => {
    const { data, success } = await executeRequest(() =>
      axios.post(AUTHENTICATION_OPTIONS_URL, { email })
    );
    return { data, success };
  }, [executeRequest]);

  const verifyMutation = useCallback(async (email: string, credential: AuthenticationResponseJSON): Promise<{ data: LoginResponse | undefined, success: boolean }> => {
    const { data, success } = await executeRequest(() =>
      axios.post(AUTHENTICATION_URL, { email, credential })
    );
    return { data, success };
  }, [executeRequest]);

  return { loading, error, optionsMutation, verifyMutation };
};

export const useWebAuthnRegistrationMutation = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const optionsMutation = useCallback(async (): Promise<{ data: PublicKeyCredentialCreationOptionsJSON | undefined, success: boolean }> => {
    const { data, success } = await executeRequest(() =>
      axios.post(REGISTRATION_OPTIONS_URL)
    );
    return { data, success };
  }, [executeRequest]);

  const verifyMutation = useCallback(async (credential: RegistrationResponseJSON, deviceName: string): Promise<{ data: WebAuthnCredentialData | undefined, success: boolean }> => {
    const { data, success } = await executeRequest(() =>
      axios.post(REGISTRATION_URL, { credential, device_name: deviceName })
    );
    return { data, success };
  }, [executeRequest]);

  return { loading, error, optionsMutation, verifyMutation };
};

export const useWebAuthnCredentials = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const list = useCallback(async (): Promise<WebAuthnCredentialData[]> => {
    const { data } = await executeRequest(() => axios.get(WEBAUTHN_CREDENTIALS_URL));
    return data || [];
  }, [executeRequest]);

  const remove = useCallback(async (id: number): Promise<{ success: boolean }> => {
    const { success } = await executeRequest(() =>
      axios.delete(`${WEBAUTHN_CREDENTIALS_URL}${id}/`)
    );
    return { success };
  }, [executeRequest]);

  return { loading, error, list, remove };
};
